/* Cómo se escriben fechas, números, teléfonos y estados en el panel. Todo en hora de Buenos
 * Aires y con las etiquetas en castellano rioplatense, sentence case. */
import type { BadgeTone, IconName } from "./ds";
import type { EstadoApertura, EstadoLead, EstadoMensaje } from "./tipos";

export const TZ = "America/Argentina/Buenos_Aires";

export const numero = (n: number | null | undefined) => (n === null || n === undefined ? "–" : n.toLocaleString("es-AR"));
export const usd = (n: number) => `US$ ${n.toLocaleString("es-AR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
export const porcentaje = (parte: number, total: number) => (total ? `${Math.round((parte / total) * 100)}%` : "0%");
export const rating = (r: number | null) => (r === null ? "–" : r.toLocaleString("es-AR", { minimumFractionDigits: 1, maximumFractionDigits: 1 }));

const fmtCorta = new Intl.DateTimeFormat("es-AR", { timeZone: TZ, day: "numeric", month: "short", hour: "2-digit", minute: "2-digit", hour12: false });
const fmtDia = new Intl.DateTimeFormat("es-AR", { timeZone: TZ, day: "numeric", month: "short", year: "numeric" });
const fmtDiaLargo = new Intl.DateTimeFormat("es-AR", { timeZone: TZ, weekday: "long", day: "numeric", month: "long" });
const fmtHora = new Intl.DateTimeFormat("es-AR", { timeZone: TZ, hour: "2-digit", minute: "2-digit", hour12: false });
const fmtClaveDia = new Intl.DateTimeFormat("en-CA", { timeZone: TZ, year: "numeric", month: "2-digit", day: "2-digit" });

const limpiar = (s: string) => s.replace(/\./g, "").replace(",", "");
export const fechaHora = (iso: string | null) => (iso ? limpiar(fmtCorta.format(new Date(iso))) : "–");
export const fecha = (iso: string | null) => (iso ? fmtDia.format(new Date(iso)).replace(/\./g, "") : "–");
export const hora = (iso: string) => fmtHora.format(new Date(iso));
export const claveDia = (iso: string) => fmtClaveDia.format(new Date(iso));
export function diaLegible(iso: string): string {
  const clave = claveDia(iso);
  const hoy = claveDia(new Date().toISOString());
  const ayer = claveDia(new Date(Date.now() - 86_400_000).toISOString());
  if (clave === hoy) return "Hoy";
  if (clave === ayer) return "Ayer";
  const t = fmtDiaLargo.format(new Date(iso));
  return t.charAt(0).toUpperCase() + t.slice(1);
}
export function hace(iso: string | null): string {
  if (!iso) return "nunca";
  const min = Math.round((Date.now() - new Date(iso).getTime()) / 60_000);
  if (min < 1) return "recién";
  if (min < 60) return `hace ${min} min`;
  const h = Math.round(min / 60);
  if (h < 24) return `hace ${h} h`;
  const d = Math.round(h / 24);
  return d === 1 ? "ayer" : `hace ${d} días`;
}

/* +5491157990766 → +54 9 11 5799-0766 · +541154594921 → +54 11 5459-4921 */
export function telefono(e164: string | null): string {
  if (!e164) return "–";
  const m = e164.match(/^\+54(9?)(11|2\d{2}|3\d{2}|2\d{3})(\d+)$/);
  if (!m) return e164;
  const [, nueve, area, local] = m;
  const corte = local!.length - 4;
  return `+54 ${nueve ? "9 " : ""}${area} ${local!.slice(0, corte)}-${local!.slice(corte)}`;
}
export const linkWhatsapp = (e164: string, texto?: string) =>
  `https://wa.me/${e164.replace(/^\+/, "")}${texto ? `?text=${encodeURIComponent(texto)}` : ""}`;
export const linkInstagram = (handle: string) => `https://instagram.com/${handle}`;
export const sinProtocolo = (url: string) => url.replace(/^https?:\/\/(www\.)?/, "").replace(/\/$/, "");

export const ESTADOS_LEAD: { valor: EstadoLead; etiqueta: string; tono: BadgeTone }[] = [
  { valor: "nueva", etiqueta: "Nueva", tono: "neutral" },
  { valor: "calificada", etiqueta: "Calificada", tono: "brand" },
  { valor: "contactada", etiqueta: "Contactada", tono: "accent" },
  { valor: "respondio", etiqueta: "Respondió", tono: "success" },
  { valor: "reunion", etiqueta: "Reunión", tono: "success" },
  { valor: "cliente", etiqueta: "Cliente", tono: "highlight" },
  { valor: "descartada", etiqueta: "Descartada", tono: "outline" },
  { valor: "baja", etiqueta: "Baja", tono: "danger" },
];
export const estadoLead = (e: EstadoLead) => ESTADOS_LEAD.find((x) => x.valor === e) ?? { valor: e, etiqueta: e, tono: "neutral" as BadgeTone };

export const ESTADOS_MENSAJE: Record<EstadoMensaje, { etiqueta: string; tono: BadgeTone }> = {
  borrador: { etiqueta: "Borrador", tono: "outline" },
  aprobado: { etiqueta: "Enviando", tono: "warning" },
  enviado: { etiqueta: "Enviado", tono: "success" },
  fallo: { etiqueta: "Falló", tono: "danger" },
  cancelado: { etiqueta: "Descartado", tono: "neutral" },
};

/* Qué sabe Chatwoot de cada apertura de WhatsApp (src/outreach/envio.ts, `estadoApertura`). */
export const ESTADOS_APERTURA: Record<EstadoApertura | "sin_confirmar", { etiqueta: string; tono: BadgeTone }> = {
  aprobada: { etiqueta: "Aprobada por Meta", tono: "success" },
  pendiente: { etiqueta: "Meta todavía no la aprobó", tono: "warning" },
  rechazada: { etiqueta: "Meta la rechazó", tono: "danger" },
  falta: { etiqueta: "No está en la bandeja de Chatwoot", tono: "danger" },
  distinta: { etiqueta: "Las variables no coinciden con la aprobada", tono: "danger" },
  sin_confirmar: { etiqueta: "Sin confirmar con Chatwoot", tono: "neutral" },
};
export const nombrePlantilla = (p: string | null) => p ?? "Sin plantilla";

export const PROVEEDORES: Record<string, string> = {
  tokko: "Tokko",
  wordpress: "WordPress",
  wix: "Wix",
  mapaprop: "Mapaprop",
  adinco: "Adinco",
  sipro: "Sipro",
  squarespace: "Squarespace",
};
export const proveedor = (p: string | null) => (p ? (PROVEEDORES[p] ?? p) : "Sin detectar");

export function motivoDescarte(m: string | null): string {
  if (!m) return "Sin motivo";
  if (m.startsWith("manual:")) return `A mano: ${m.slice(7).trim()}`;
  return (
    {
      sin_contacto: "Sin ningún dato de contacto",
      cadena_grande: "Cadena grande, fuera del perfil",
      google_closed_permanently: "Cerrada según Google",
      google_closed_temporarily: "Cerrada temporalmente según Google",
    } as Record<string, string>
  )[m] ?? m;
}

/* Las señales del puntaje (src/puntuar.ts), en palabras. */
export const SENALES: Record<string, { etiqueta: string; icono: IconName }> = {
  whatsapp: { etiqueta: "WhatsApp móvil", icono: "whatsapp" },
  telefono: { etiqueta: "Teléfono fijo", icono: "phone" },
  email: { etiqueta: "Email", icono: "mail" },
  sitio_propio: { etiqueta: "Sitio propio", icono: "globe" },
  sitio_tercero: { etiqueta: "Sitio en otra plataforma", icono: "globe" },
  instagram: { etiqueta: "Instagram", icono: "camera" },
  resenas_ideal: { etiqueta: "Reseñas en Google: entre 5 y 60", icono: "message-square" },
  resenas_grande: { etiqueta: "Reseñas en Google: entre 60 y 200", icono: "message-square" },
  resenas_muy_grande: { etiqueta: "Reseñas en Google: más de 200", icono: "message-square" },
  resenas_pocas: { etiqueta: "Pocas reseñas en Google", icono: "message-square" },
  buen_rating: { etiqueta: "Rating de 4,2 o más", icono: "trending-up" },
  propiedades_ideal: { etiqueta: "Entre 8 y 30 propiedades", icono: "house" },
  propiedades_grande: { etiqueta: "Entre 30 y 80 propiedades", icono: "house" },
  propiedades_pocas: { etiqueta: "Pocas propiedades", icono: "house" },
  propiedades_casi_nada: { etiqueta: "Casi sin propiedades", icono: "house" },
  zona_prioritaria: { etiqueta: "Zona de prioridad 1", icono: "map-pin" },
  zona_secundaria: { etiqueta: "Zona de prioridad 2", icono: "map-pin" },
};
